import { useEffect, useState } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  ButtonPrimary,
  Form,
  InputCheckbox,
  Modal,
  ModalActions,
  ModalProps
} from '@lambdacurry/component-library';
import { addressInitialValues, nameInitialValues } from 'lib/forms/initialValues';
import { CreateAddressInput, UpdateAddressInput } from 'lib/home/generated/types/api';
import { nameValidation, addressValidation } from 'lib/forms/validation';
import { AddressInputGroup, NameInputGroup } from '../..';

type FormValues = CreateAddressInput | UpdateAddressInput;

export interface ShippingAddressFormModalProps<T> extends ModalProps {
  address?: T;
  isOpen: boolean;
  onCancel?: () => void;
  onSubmit: (values: T, formikActions: FormikHelpers<T>) => void;
}

export const ShippingAddressFormModal = <T extends FormValues>({
  address,
  onCancel,
  onSubmit,
  isOpen,
  ...props
}: ShippingAddressFormModalProps<T>) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const handleSubmit = async (values: T, formikActions: FormikHelpers<T>) => onSubmit(values, formikActions);

  const handleCancel = (formikProps: FormikProps<T>) => setIsModalOpen(false);

  const initialValues: T = {
    ...nameInitialValues,
    ...addressInitialValues,
    ...(address ? address : {})
  } as T;

  const validationSchema = Yup.object({ ...nameValidation, ...addressValidation });

  return (
    <Modal id={`shipping-address-form-modal`} isOpen={isModalOpen} {...props}>
      <Form<T> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {formikProps => (
          <>
            <h1>{address ? <>Edit</> : <>Add</>} Shipping Address</h1>

            <NameInputGroup />
            <AddressInputGroup />

            {!address && (
              <InputCheckbox name="is_default" label="Set as default shipping address for all future orders" />
            )}

            <ModalActions>
              <ButtonPrimary type="submit" data-cy="submit">
                Save
              </ButtonPrimary>
              <Button onClick={() => handleCancel(formikProps)} data-cy="cancel">
                Cancel
              </Button>
            </ModalActions>
          </>
        )}
      </Form>
    </Modal>
  );
};
